import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { blogAPI } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const AdminBlogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '', slug: '', cover_image: '', content: '', category: '', tags: [], meta_title: '', meta_description: '', published: true
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) navigate('/admin/login');
    else fetchBlogs();
  }, [navigate]);

  const fetchBlogs = async () => {
    try {
      const response = await blogAPI.getAll(false);
      setBlogs(response.data);
    } catch (error) {
      toast.error('Bloglar yüklenemedi');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBlog) {
        await blogAPI.update(editingBlog.id, formData);
        toast.success('Blog güncellendi');
      } else {
        await blogAPI.create(formData);
        toast.success('Blog oluşturuldu');
      }
      setShowDialog(false);
      setEditingBlog(null);
      setFormData({ title: '', slug: '', cover_image: '', content: '', category: '', tags: [], meta_title: '', meta_description: '', published: true });
      fetchBlogs();
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Silmek istediğinizden emin misiniz?')) return;
    try {
      await blogAPI.delete(id);
      toast.success('Blog silindi');
      fetchBlogs();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  const openEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({ ...blog });
    setShowDialog(true);
  };

  return (
    <div className="min-h-screen bg-light" data-testid="admin-blogs-page">
      <nav className="bg-navy text-white p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/admin/dashboard" className="flex items-center space-x-2 hover:text-orange">
            <ArrowLeft size={20} />
            <span>Dashboard'a Dön</span>
          </Link>
          <h1 className="text-2xl font-bold">Blog Yönetimi</h1>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <Button data-testid="add-blog-button" onClick={() => { setEditingBlog(null); setFormData({ title: '', slug: '', cover_image: '', content: '', category: '', tags: [], meta_title: '', meta_description: '', published: true }); setShowDialog(true); }} className="mb-6 bg-orange hover:bg-orange/90">
          <Plus className="mr-2" size={20} />
          Yeni Blog Ekle
        </Button>

        <div className="space-y-4">
          {blogs.map((blog) => (
            <div key={blog.id} className="bg-white p-6 rounded-lg shadow-sm flex justify-between items-center" data-testid={`blog-item-${blog.id}`}>
              <div>
                <h3 className="text-lg font-bold text-navy">{blog.title}</h3>
                <p className="text-sm text-slate-500">{blog.slug}</p>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => openEdit(blog)} className="p-2 text-navy hover:bg-slate-100 rounded"><Pencil size={20} /></button>
                <button onClick={() => handleDelete(blog.id)} className="p-2 text-red-600 hover:bg-slate-100 rounded"><Trash2 size={20} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingBlog ? 'Blog Düzenle' : 'Yeni Blog'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="block text-sm font-semibold mb-1">Başlık</label><input required type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">Slug</label><input required type="text" value={formData.slug} onChange={(e) => setFormData({ ...formData, slug: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">Kapak Görseli URL</label><input required type="url" value={formData.cover_image} onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">İçerik</label><textarea required rows={6} value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">Kategori</label><input required type="text" value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">Meta Title</label><input required type="text" value={formData.meta_title} onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="block text-sm font-semibold mb-1">Meta Description</label><input required type="text" value={formData.meta_description} onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })} className="w-full px-3 py-2 border rounded-lg" /></div>
            <div><label className="flex items-center space-x-2"><input type="checkbox" checked={formData.published} onChange={(e) => setFormData({ ...formData, published: e.target.checked })} /><span className="text-sm font-semibold">Yayınla</span></label></div>
            <Button type="submit" className="w-full bg-orange hover:bg-orange/90">Kaydet</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBlogs;
